import { ReactComponent as ArrowLeftIcon } from 'img/icons/vkui/arrow_left_outline_24.svg';
import { ReactComponent as ArrowRightIcon } from 'img/icons/vkui/arrow_right_outline_24.svg';
import React, { type FC } from 'react';
import { useSelector } from 'react-redux';
import {
    familyAutoUploadEmptyCard,
    familyFeatureDocuments,
    familyUpdatedEmptystate,
} from 'reactApp/appHelpers/featuresHelpers/features/familyFunctions';
import { useShowFamilyCalendarHook } from 'reactApp/hooks/useFamilyShowHook';
import { useIsFreemium } from 'reactApp/hooks/useFreemium';
import { getFamilyContentCategory } from 'reactApp/modules/familyContent/familyContent.selectors';
import { FamilyContentCategoryType } from 'reactApp/modules/familyContent/familyContent.types';
import { FamilyDocumentsEmptyState } from 'reactApp/ui/FamilyContentEmpty/FamilyContentEmptyCards/FamilyDocuments';
import { FamilySubscription } from 'reactApp/ui/FamilyContentEmpty/FamilyContentEmptyCards/FamilySubscription';
import { SharedAlbums } from 'reactApp/ui/FamilyContentEmpty/FamilyContentEmptyCards/SharedAlbums';
import { SharedBusiness } from 'reactApp/ui/FamilyContentEmpty/FamilyContentEmptyCards/SharedBusiness';
import { SharedFolders } from 'reactApp/ui/FamilyContentEmpty/FamilyContentEmptyCards/SharedFolders';
import { SharedMemories } from 'reactApp/ui/FamilyContentEmpty/FamilyContentEmptyCards/SharedMemories';
import { FreeMode, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import styles from './FamilyContentEmpty.css';
import { SharedCalendar } from './FamilyContentEmptyCards/SharedCalendar';
import newEmptyStyles from './FamilyContentNewEmpty.css';
import { FamilyAutoUpload } from './FamilyContentNewEmptyCards/FamilyAutoUpload';
import { FamilyDocumentsEmptyState as FamilyDocumentsEmptyStateNew } from './FamilyContentNewEmptyCards/FamilyDocuments';
import { FamilySubscription as FamilySubscriptionNew } from './FamilyContentNewEmptyCards/FamilySubscription';
import { SharedCalendar as SharedCalendarNew } from './FamilyContentNewEmptyCards/SharedCalendar';
import { SharedFolders as SharedFoldersNew } from './FamilyContentNewEmptyCards/SharedFolders';

const SHARED_MAP: Record<FamilyContentCategoryType, FC> = {
    [FamilyContentCategoryType.albums]: () => {
        return (
            <>
                <SharedAlbums />
                <SharedMemories />
            </>
        );
    },

    [FamilyContentCategoryType.folders]: () => {
        return (
            <>
                <SharedFolders />
                <SharedBusiness />
            </>
        );
    },
};

export const FamilyContentEmpty: React.FC = () => {
    const type: FamilyContentCategoryType = useSelector(getFamilyContentCategory);
    const Shared = SHARED_MAP[type];
    const showFamilyCalendar = useShowFamilyCalendarHook();
    const isFreemium = useIsFreemium();

    if (familyUpdatedEmptystate && type === FamilyContentCategoryType.folders) {
        return (
            <div className={styles.root} data-qa-id="family-empty">
                <div className={`${styles.list} ${newEmptyStyles.slider}`}>
                    <Swiper
                        slidesPerView="auto"
                        spaceBetween={12}
                        navigation={{
                            nextEl: `.${newEmptyStyles.arrowNext}`,
                            prevEl: `.${newEmptyStyles.arrowPrev}`,
                            disabledClass: newEmptyStyles.arrowDisabled,
                        }}
                        modules={[Navigation, FreeMode]}
                    >
                        <SwiperSlide>
                            <FamilySubscriptionNew />
                        </SwiperSlide>
                        {familyAutoUploadEmptyCard && isFreemium ? (
                            <SwiperSlide>
                                <FamilyAutoUpload />
                            </SwiperSlide>
                        ) : null}
                        <SwiperSlide>
                            <SharedFoldersNew />
                        </SwiperSlide>
                        {showFamilyCalendar ? (
                            <SwiperSlide>
                                <SharedCalendarNew paySource="family-empty-state" />
                            </SwiperSlide>
                        ) : null}
                        {familyFeatureDocuments ? (
                            <SwiperSlide>
                                <FamilyDocumentsEmptyStateNew />
                            </SwiperSlide>
                        ) : null}
                    </Swiper>
                    <div className={newEmptyStyles.arrowPrev}>
                        <ArrowLeftIcon />
                    </div>
                    <div className={newEmptyStyles.arrowNext}>
                        <ArrowRightIcon />
                    </div>
                </div>
            </div>
        );
    }

    const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
        event.currentTarget.scrollLeft += event.deltaY;
    };

    return (
        <div className={styles.root} data-qa-id="family-empty">
            <div className={styles.list} onWheel={handleWheel}>
                <Shared />
                {showFamilyCalendar && <SharedCalendar paySource={`family-preview-${type}`} />}
                {familyFeatureDocuments ? <FamilyDocumentsEmptyState /> : <FamilySubscription />}
            </div>
        </div>
    );
};
